import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import DemoModeEntry from '../components/demo/DemoModeEntry';



function AuthForm({ 
  isRegisterMode, 
  onToggleMode, 
  onSubmit, 
  onChange, 
  formData, 
  error, 
  loading,
  onLogoClick 
}: {
  isRegisterMode: boolean;
  onToggleMode: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  formData: { 
    username: string; 
    password: string; 
    name?: string; 
    address?: string; 
    telephone?: string; 
  };
  error: string;
  loading: boolean;
  onLogoClick: () => void;
}) {
  return (
    <div className={`w-full mx-4 p-8 rounded-2xl backdrop-blur-xl bg-white/20 shadow-2xl border border-white/10 login-container transition-all duration-500 ${
      isRegisterMode ? 'max-w-lg' : 'max-w-md'
    }`}>
      <div className="flex flex-col items-center mb-8">
        <button 
          onClick={onLogoClick}
          className="mb-2 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg"
          type="button"
        >
          <img src="/logo-bvg-white.png" alt="Big Vision Games" className="w-9 h-auto" />
        </button>
        <h1 className="text-3xl text-white font-thin">Big Vision Games</h1>
        <p className="text-white/80 mt-2 font-thin">
          {isRegisterMode ? 'Create your account' : 'Sign in to your account'}
        </p>
      </div>

      <form className="space-y-6" onSubmit={onSubmit}>
        {error && (
          <div className="p-3 text-sm text-red-200 bg-red-500/20 border border-red-500/20 rounded-lg">
            {error}
          </div>
        )}

        {/* Common fields - Email and Password first */}
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-white mb-2">
            {isRegisterMode ? 'Email' : 'Username'}
          </label>
          <input
            id="username"
            name="username"
            type={isRegisterMode ? 'email' : 'text'}
            required
            value={formData.username}
            onChange={onChange}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-white/50"
            placeholder={isRegisterMode ? 'Enter your email' : 'Enter your username'}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            value={formData.password}
            onChange={onChange}
            minLength={isRegisterMode ? 8 : undefined}
            autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-white/50"
            placeholder={isRegisterMode ? 'Choose a password (min. 8 characters)' : 'Enter your password'}
          />
        </div>

        {/* Registration-only fields */}
        <div className={`overflow-hidden transition-all duration-500 ${
          isRegisterMode ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Full Name
              </label>
              <input
                name="name"
                type="text"
                required={isRegisterMode}
                value={formData.name || ''}
                onChange={onChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-white/50"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Address
              </label>
              <textarea
                name="address"
                required={isRegisterMode}
                value={formData.address || ''}
                onChange={onChange}
                rows={2}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-white/50"
                placeholder="Enter your address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Telephone
              </label>
              <input
                name="telephone"
                type="tel"
                required={isRegisterMode}
                value={formData.telephone || ''}
                onChange={onChange}
                minLength={7}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-white/50"
                placeholder="Enter your telephone number"
              />
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent"
          >
            {loading ? 
              (isRegisterMode ? 'Creating Account...' : 'Signing in...') : 
              (isRegisterMode ? 'Create Account' : 'Sign In')
            }
          </button>

          <button
            type="button"
            onClick={onToggleMode}
            className="w-full py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent"
          >
            {isRegisterMode ? 'Already have an account? Sign In' : 'Need an account? Register'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showSplash, setShowSplash] = useState(true);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    address: '',
    telephone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if we should auto-switch to registration mode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldRegister = urlParams.get('register') === 'true';
    
    if (shouldRegister) {
      // Switch to registration mode after animations complete
      const timer = setTimeout(() => {
        setIsRegisterMode(true);
      }, 3800); // 300ms after login form appears
      
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    // Show login form after splash text fades out
    const loginTimer = setTimeout(() => {
      setShowLoginForm(true);
    }, 3500);
    
    // Hide splash text
    const splashTimer = setTimeout(() => {
      setShowSplash(false);
    }, 7000);

    return () => {
      clearTimeout(loginTimer);
      clearTimeout(splashTimer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegisterMode) {
        // Registration flow
        if (!formData.username || !formData.password || !formData.name || !formData.address || !formData.telephone) {
          throw new Error('All fields are required');
        }

        // Add telephone number length validation
        const MIN_TELEPHONE_LENGTH = 7;
        if (formData.telephone.length < MIN_TELEPHONE_LENGTH) {
          throw new Error(`Telephone number must be at least ${MIN_TELEPHONE_LENGTH} digits.`);
        }

        // 1. Hash the password
        const { data: hashedPassword, error: hashError } = await supabase
          .rpc('hash_password', {
            password: formData.password
          });

        if (hashError) throw hashError;

        // 2. Create a login entry first to get the login_id
        const { data: loginData, error: loginError } = await supabase
          .from('logins')
          .insert({
            username: formData.username,
            password: hashedPassword,
            role: 'visionary'
          })
          .select('id')
          .single();

        if (loginError) throw loginError;
        if (!loginData || !loginData.id) throw new Error('Failed to create login entry or retrieve login ID.');

        // 3. Create a visionary with the login_id
        const { error: visionaryError } = await supabase
          .from('visionaries')
          .insert({
            name: formData.name,
            email: formData.username,
            address: formData.address,
            telephone: formData.telephone,
            loyalty_points: 0,
            login_id: loginData.id
          })
          .single();

        if (visionaryError) throw visionaryError;

        // Switch to login mode and clear form
        setIsRegisterMode(false);
        setFormData({
          username: formData.username, // Keep email for login
          password: '',
          name: '',
          address: '',
          telephone: '',
        });
        alert('Registration successful! Please log in with your email and password.');
      } else {
        // Login flow
        const { user, role } = await login(formData.username, formData.password);
        if (!user) throw new Error('Login failed');

        if (role === 'visionary') {
          navigate('/shop');
        } else {
          navigate('/make-order');
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(isRegisterMode ? errorMessage : 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => {
      let value = e.target.value;
      if (e.target.name === 'username') {
        value = value.toLowerCase();
      } else if (e.target.name === 'telephone') {
        // Allow only digits for telephone number
        value = value.replace(/\D/g, '');
      }
      return {
        ...prev,
        [e.target.name]: value
      };
    });
  };

  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode);
    setError('');
    // Clear password when switching modes for security
    setFormData(prev => ({ ...prev, password: '' }));
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="splash-screen" />
      {showSplash && (
        <div className="fixed inset-0 z-[2] flex flex-col items-center justify-center pointer-events-none">
          <div className="splash-content">
            <img src="/logo-bvg-white.png" alt="Big Vision Games" className="splash-logo" />
            <h1 className="splash-text font-thin">BIG VISION GAMES</h1>
            <p className="splash-subtext font-thin">Gaming Beyond Boundaries</p>
          </div>
        </div>
      )}
      {showLoginForm && (
        <div className="relative z-[3] flex items-center justify-center w-full">
          <div className="flex flex-col items-center">
            <AuthForm
              isRegisterMode={isRegisterMode}
              onToggleMode={toggleMode}
              onSubmit={handleSubmit}
              onChange={handleChange}
              formData={formData}
              error={error}
              loading={loading}
              onLogoClick={() => navigate('/')}
            />
            {!isRegisterMode && <DemoModeEntry />}
          </div>
        </div>
      )}
    </div>
  );
}
